import { useApolloClient } from "@apollo/client";
import { ChevronDownIcon, ChevronLeft, ChevronRight, ChevronUpIcon } from "lucide-react";
import { bookingsChartQuery } from "../BookingsChart";
import { type IntervalType, CHART_BOOKING_STATUSES } from "../BookingsChart/constants";
import {
  calculateNextDate,
  calculatePreviousDate,
  getBucketCountForInterval,
  getDateRangeForInterval,
} from "../BookingsChart/helpers";
import { type ChartVisibilityState } from "../BookingsForecast";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/ui/button";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { toDisplayDate, toIsoDate } from "@/lib/utils/dates";

interface BookingsChartNavigationProps {
  referenceDate: Date;
  visibility: ChartVisibilityState;
  onChartStateChange: (state: ChartVisibilityState) => void;
  intervalType: IntervalType;
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
  onResetToToday: () => void;
}

export function BookingsChartNavigation({
  referenceDate,
  visibility,
  onChartStateChange,
  intervalType,
  onNavigateNext,
  onNavigatePrevious,
  onResetToToday,
}: BookingsChartNavigationProps) {
  const client = useApolloClient();
  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const dateRange = getDateRangeForInterval({
    referenceDate,
    intervalType,
    bucketCount: getBucketCountForInterval(intervalType),
  });

  const handlePreviousPeriod = () => {
    onNavigatePrevious();
  };

  const handleNextPeriod = () => {
    onNavigateNext();
  };

  const handleResetToToday = () => {
    onResetToToday();
  };

  const handlePreviousHover = () => {
    const previousDate = calculatePreviousDate(referenceDate, intervalType);
    prefetchData(previousDate);
  };

  const handleNextHover = () => {
    const nextDate = calculateNextDate(referenceDate, intervalType);
    prefetchData(nextDate);
  };

  const prefetchData = (date: Date) => {
    const dateRange = getDateRangeForInterval({
      referenceDate: date,
      intervalType,
      bucketCount: getBucketCountForInterval(intervalType),
    });
    void client.query({
      query: bookingsChartQuery,
      variables: {
        businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : undefined,
        plannedShippingDateAfter: toIsoDate(dateRange.start),
        plannedShippingDateBefore: toIsoDate(dateRange.end),
        status: CHART_BOOKING_STATUSES,
      },
    });
  };

  return (
    <div className="flex p-4 justify-end items-center gap-2 flex-wrap">
      <Button variant="outline" onClick={handleResetToToday}>
        Today
      </Button>
      <Tooltip label={`View previous ${getBucketCountForInterval(intervalType)} ${intervalType}`} asChild>
        <Button
          variant="outline"
          onClick={handlePreviousPeriod}
          onMouseEnter={handlePreviousHover}
          className="h-10 w-10"
        >
          <ChevronLeft />
        </Button>
      </Tooltip>
      <span className="text-sm font-medium min-w-28 text-center">
        {toDisplayDate(dateRange.start, "MMM d")} - {toDisplayDate(dateRange.end, "MMM d")}
      </span>
      <Tooltip label={`View next ${getBucketCountForInterval(intervalType)} ${intervalType}`} asChild>
        <Button variant="outline" onClick={handleNextPeriod} onMouseEnter={handleNextHover} className="h-10 w-10">
          <ChevronRight />
        </Button>
      </Tooltip>
      <Button
        variant="outline"
        onClick={() => {
          onChartStateChange(visibility === "expanded" ? "collapsed" : "expanded");
        }}
        className="w-40"
      >
        {visibility === "expanded" ? (
          <>
            <ChevronUpIcon className="h-4 w-4" /> Hide Forecast
          </>
        ) : (
          <>
            <ChevronDownIcon className="h-4 w-4" /> Show Forecast
          </>
        )}
      </Button>
    </div>
  );
}
